import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  FileText, Plus, Trash, Check, CircleNotch, ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function ProformaBuilderPage() {
  const { id: docId } = useParams();
  const isEditMode = !!docId;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [isFirstProforma, setIsFirstProforma] = useState(false);
  const [startingNumber, setStartingNumber] = useState(1);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    client_name: '',
    client_address: '',
    client_vat: '',
    client_fiscal_code: '',
    client_email: '',
    iva_rate: 22,
    payment_terms: 'Bonifico bancario 30gg',
    payment_details: '',
    notes: ''
  });

  const [items, setItems] = useState([
    { description: '', unit: 'pz', quantity: 1, price: '' }
  ]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(Array.isArray(data) ? data : [])
    } catch (err) { console.error(err); }
  }, []);

  const checkFirstProforma = useCallback(async () => {
    if (isEditMode) return;
    try {
      const { data } = await axios.get(`${API}/proforma`);
      if ((data || []).length === 0) setIsFirstProforma(true);
    } catch (err) { console.error(err); }
  }, [isEditMode]);

  const fetchDoc = useCallback(async () => {
    if (!docId) return;
    try {
      const { data } = await axios.get(`${API}/proforma/${docId}`);
      setForm({
        date: data.date || '',
        due_date: data.due_date || '',
        client_name: data.client_name || '',
        client_address: data.client_address || '',
        client_vat: data.client_vat || '',
        client_fiscal_code: data.client_fiscal_code || '',
        client_email: data.client_email || '',
        iva_rate: data.iva_rate ?? 22,
        payment_terms: data.payment_terms || '',
        payment_details: data.payment_details || '',
        notes: data.notes || ''
      });
      setItems(data.items?.length ? data.items : [{ description: '', unit: 'pz', quantity: 1, price: '' }]);
      setSelectedClientId(data.client_id || '');
    } catch (err) {
      toast.error(t('proforma.loadError'));
      navigate('/proforma');
    }
  }, [docId, navigate, t]);

  useEffect(() => { fetchClients(); fetchDoc(); checkFirstProforma(); }, [fetchClients, fetchDoc, checkFirstProforma]);

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    const client = (clients || []).find(c => c.id === clientId);
    if (client) {
      setForm(f => ({
        ...f,
        client_name: client.name || '',
        client_address: client.address || '',
        client_vat: client.vat_number || '',
        client_fiscal_code: client.fiscal_code || '',
        client_email: client.email || ''
      }));
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', unit: 'pz', quantity: 1, price: '' }]);
  };

  const removeItem = (index) => {
    if ((items || []).length <= 1) return;
    setItems((items || []).filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const subtotal = (items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0), 0);
  const ivaAmount = subtotal * (parseFloat(form.iva_rate) || 0) / 100;
  const total = subtotal + ivaAmount;

  const handleSave = async () => {
    if (!form.client_name) {
      toast.error(t('proforma.selectClient'));
      return;
    }
    if ((items || []).length === 0 || !items[0].description) {
      toast.error(t('proforma.addItem'));
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      client_id: selectedClientId,
      items: (items || []).filter(i => i.description),
      ...(isFirstProforma && startingNumber > 1 ? { starting_number: startingNumber } : {})
    };

    try {
      if (isEditMode) {
        await axios.put(`${API}/proforma/${docId}`, payload);
        toast.success(t('proforma.updated'));
      } else {
        await axios.post(`${API}/proforma`, payload);
        toast.success(t('proforma.created'));
      }
      navigate('/proforma');
    } catch (err) {
      toast.error(isEditMode ? t('proforma.updateError') : t('proforma.createError'));
    }
    setSaving(false);
  };

  const paymentOptions = [
    'Bonifico bancario 30gg',
    'Bonifico bancario 60gg',
    'Rimessa diretta',
    'Contanti alla consegna',
    'Assegno bancario',
    'Carta di credito'
  ];

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/proforma')} data-testid="back-to-proforma">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0A0F1A] flex items-center gap-2">
            <FileText size={24} weight="duotone" className="text-[#0033A0]" />
            {isEditMode ? t('proforma.editProforma') : t('proforma.newProforma')}
          </h1>
        </div>
      </div>

      {/* Starting number for first proforma */}
      {isFirstProforma && !isEditMode && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">{t('proforma.firstDocNote')}</p>
              <p className="text-xs text-amber-600 mt-1">{t('proforma.firstDocHint')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-amber-800 whitespace-nowrap">N. partenza:</Label>
              <Input
                type="number" min="1"
                value={startingNumber}
                onChange={e => setStartingNumber(parseInt(e.target.value) || 1)}
                className="w-20 bg-white"
                data-testid="proforma-starting-number"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('proforma.section1')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{t('proforma.selectClient')}</Label>
              <Select value={selectedClientId} onValueChange={handleClientSelect}>
                <SelectTrigger className="mt-1" data-testid="proforma-client-select">
                  <SelectValue placeholder={t('proforma.selectClientPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(clients || []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('proforma.date')}</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="proforma-date" />
            </div>
            <div>
              <Label className="text-xs">{t('proforma.dueDate')}</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="proforma-due-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('proforma.client')}</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1" data-testid="proforma-client-name" />
            </div>
            <div>
              <Label className="text-xs">{t('common.address')}</Label>
              <Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} className="mt-1" data-testid="proforma-client-address" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">P.IVA</Label>
              <Input value={form.client_vat} onChange={e => setForm({ ...form, client_vat: e.target.value })} className="mt-1" data-testid="proforma-client-vat" />
            </div>
            <div>
              <Label className="text-xs">C.F.</Label>
              <Input value={form.client_fiscal_code} onChange={e => setForm({ ...form, client_fiscal_code: e.target.value })} className="mt-1" data-testid="proforma-client-cf" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} className="mt-1" data-testid="proforma-client-email" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('proforma.section2')}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="proforma-add-item">
            <Plus size={16} className="mr-1" />
            {t('proforma.addItem')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(items || []).map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-[#F4F6F8] rounded-lg" data-testid={`proforma-item-${index}`}>
              <span className="text-xs font-bold text-[#0033A0] mt-2 w-6 text-center">{index + 1}</span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-5">
                  <Label className="text-[10px] text-[#4A5568]">{t('proforma.description')}</Label>
                  <Input
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    className="mt-0.5 bg-white text-sm"
                    placeholder="Descrizione articolo..."
                    data-testid={`proforma-item-desc-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('proforma.unit')}</Label>
                  <Select value={item.unit} onValueChange={v => updateItem(index, 'unit', v)}>
                    <SelectTrigger className="mt-0.5 bg-white text-sm" data-testid={`proforma-item-unit-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pz">pz</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="mq">mq</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="nr">nr</SelectItem>
                      <SelectItem value="ore">ore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[10px] text-[#4A5568]">{t('proforma.quantity')}</Label>
                  <Input
                    type="number" min="1"
                    value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`proforma-item-qty-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('proforma.unitPrice')}</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={item.price}
                    onChange={e => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`proforma-item-price-${index}`}
                  />
                </div>
                <div className="sm:col-span-1 text-right pt-5">
                  <span className="text-sm font-medium text-[#0A0F1A]">
                    {formatCurrency((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))}
                  </span>
                </div>
                <div className="sm:col-span-1 flex items-end">
                  {(items || []).length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeItem(index)} className="text-red-500 hover:bg-red-50 h-9 w-9" data-testid={`proforma-item-remove-${index}`}>
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Totals */}
          <div className="border-t border-[#E2E8F0] pt-4 mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#4A5568]">{t('proforma.subtotal')}</span>
              <span className="font-medium" data-testid="proforma-subtotal">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[#4A5568]">IVA</span>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={form.iva_rate}
                  onChange={e => setForm({ ...form, iva_rate: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs text-center"
                  data-testid="proforma-iva-rate"
                />
                <span className="text-[#4A5568]">%</span>
              </div>
              <span className="font-medium" data-testid="proforma-iva-amount">{formatCurrency(ivaAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-[#0033A0] pt-2">
              <span className="text-[#0033A0]">{t('proforma.total')}</span>
              <span className="text-[#0033A0]" data-testid="proforma-total">{formatCurrency(total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment & Notes */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('proforma.section3')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('proforma.paymentTerms')}</Label>
              <Select value={form.payment_terms} onValueChange={v => setForm({ ...form, payment_terms: v })}>
                <SelectTrigger className="mt-1" data-testid="proforma-payment-terms">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('proforma.paymentDetails')}</Label>
              <Input
                value={form.payment_details}
                onChange={e => setForm({ ...form, payment_details: e.target.value })}
                className="mt-1"
                placeholder="IBAN, coordinate bancarie..."
                data-testid="proforma-payment-details"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('proforma.notes')}</Label>
            <Textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder={t('proforma.notesPlaceholder')}
              className="mt-1 border-[#E2E8F0]"
              data-testid="proforma-notes"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/proforma')} data-testid="proforma-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white min-w-[180px]"
          data-testid="proforma-save"
        >
          {saving ? (
            <><CircleNotch size={18} className="mr-2 animate-spin" /> {isEditMode ? t('proforma.saving') : t('proforma.creating')}</>
          ) : (
            <><Check size={18} className="mr-2" /> {isEditMode ? t('proforma.saveProforma') : t('proforma.createProforma')}</>
          )}
        </Button>
      </div>
    </div>
  );
}

